import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useBoxes } from '@/lib/api/boxes';
import { auditedCreateBox } from '@/lib/api/boxAudit';
import { BoxFormFields, boxFormErrors, boxToForm } from './box-form';

/** Admin-only "Add box" — the dashboard is the source of truth for the fleet,
 *  so new boxes are created here (the agent no longer seeds from a file).
 *  Status starts UNKNOWN and resolves on the agent's next 5-min cycle. */
export function AddBoxDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(boxToForm());
  const [saving, setSaving] = useState(false);
  const [boxes] = useBoxes();

  const errors = boxFormErrors(form, boxes);

  const onCreate = async () => {
    if (errors.name || errors.ip) return;
    setSaving(true);
    try {
      await auditedCreateBox({
        name: form.name,
        ip: form.ip,
        brand: form.brand,
        group: form.group,
        tv: form.tv,
      });
      toast.success(`${form.name.trim()} added — status on the next cycle`);
      setOpen(false);
    } catch (error) {
      console.error('create box failed', error);
      toast.error('Failed to add box');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setForm(boxToForm());
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="mr-1 h-4 w-4" />
          Add box
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add box</DialogTitle>
          <DialogDescription>
            Name must be unique — the headend joins on it. Status shows UNKNOWN
            until the agent&apos;s next poll.
          </DialogDescription>
        </DialogHeader>

        <BoxFormFields
          form={form}
          onChange={setForm}
          errors={errors}
          idPrefix="add-box"
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={onCreate}
            disabled={saving || !!errors.name || !!errors.ip}
          >
            {saving ? 'Adding…' : 'Add box'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
